// =============================================================================
// ESPOLIDAR WEB ADMIN - DEVICES HOOKS
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { devicesApi, zonesApi, triggersApi, eventsApi, configApi } from '../services/api'
import { wsService } from '../services/websocket'
import type { ZoneCreate, ZoneUpdate, TriggerCreate } from '../types'
import toast from 'react-hot-toast'

// Devices
export function useDevices() {
  return useQuery({
    queryKey: ['devices'],
    queryFn: devicesApi.list,
  })
}

export function useDevice(deviceId: string) {
  return useQuery({
    queryKey: ['device', deviceId],
    queryFn: () => devicesApi.get(deviceId),
    enabled: !!deviceId,
  })
}

export function useUpdateDevice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ deviceId, data }: { deviceId: string; data: Partial<{ name: string; description: string }> }) =>
      devicesApi.update(deviceId, data),
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['device', deviceId] })
      toast.success('Dispositivo aggiornato')
    },
    onError: () => {
      toast.error('Errore aggiornamento dispositivo')
    },
  })
}

export function useDeleteDevice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: devicesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      toast.success('Dispositivo eliminato')
    },
    onError: () => {
      toast.error('Errore eliminazione dispositivo')
    },
  })
}

// Zones
export function useDeviceZones(deviceId: string) {
  return useQuery({
    queryKey: ['zones', deviceId],
    queryFn: () => zonesApi.listByDevice(deviceId),
    enabled: !!deviceId,
  })
}

export function useCreateZone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ deviceId, data }: { deviceId: string; data: ZoneCreate }) =>
      zonesApi.create(deviceId, data),
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: ['zones', deviceId] })
      queryClient.invalidateQueries({ queryKey: ['device', deviceId] })
      // Notifica il player per sincronizzare la configurazione
      wsService.notifyConfigUpdate(deviceId)
      toast.success('Zona creata e sincronizzata')
    },
    onError: () => {
      toast.error('Errore creazione zona')
    },
  })
}

export function useUpdateZone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ zoneId, data }: { zoneId: number; data: ZoneUpdate; deviceId?: string }) =>
      zonesApi.update(zoneId, data),
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: ['zones'] })
      queryClient.invalidateQueries({ queryKey: ['device'] })
      // Notifica il player per sincronizzare la configurazione
      if (deviceId) {
        wsService.notifyConfigUpdate(deviceId)
      }
      toast.success('Zona aggiornata e sincronizzata')
    },
    onError: () => {
      toast.error('Errore aggiornamento zona')
    },
  })
}

export function useDeleteZone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ zoneId }: { zoneId: number; deviceId?: string }) =>
      zonesApi.delete(zoneId),
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: ['zones'] })
      queryClient.invalidateQueries({ queryKey: ['device'] })
      // Notifica il player per sincronizzare la configurazione
      if (deviceId) {
        wsService.notifyConfigUpdate(deviceId)
      }
      toast.success('Zona eliminata e sincronizzata')
    },
    onError: () => {
      toast.error('Errore eliminazione zona')
    },
  })
}

// Triggers
export function useDeviceTriggers(deviceId: string) {
  return useQuery({
    queryKey: ['triggers', deviceId],
    queryFn: () => triggersApi.listByDevice(deviceId),
    enabled: !!deviceId,
  })
}

export function useCreateTrigger() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ deviceId, data }: { deviceId: string; data: TriggerCreate }) =>
      triggersApi.create(deviceId, data),
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: ['triggers', deviceId] })
      // Notifica il player per sincronizzare la configurazione
      wsService.notifyConfigUpdate(deviceId)
      toast.success('Trigger creato e sincronizzato')
    },
    onError: () => {
      toast.error('Errore creazione trigger')
    },
  })
}

export function useUpdateTrigger() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ triggerId, data }: { triggerId: number; data: Partial<TriggerCreate>; deviceId?: string }) =>
      triggersApi.update(triggerId, data),
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: ['triggers'] })
      // Notifica il player per sincronizzare la configurazione
      if (deviceId) {
        wsService.notifyConfigUpdate(deviceId)
      }
      toast.success('Trigger aggiornato e sincronizzato')
    },
    onError: () => {
      toast.error('Errore aggiornamento trigger')
    },
  })
}

export function useDeleteTrigger() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ triggerId }: { triggerId: number; deviceId?: string }) =>
      triggersApi.delete(triggerId),
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: ['triggers'] })
      // Notifica il player per sincronizzare la configurazione
      if (deviceId) {
        wsService.notifyConfigUpdate(deviceId)
      }
      toast.success('Trigger eliminato e sincronizzato')
    },
    onError: () => {
      toast.error('Errore eliminazione trigger')
    },
  })
}

// Events
export function useDeviceEvents(deviceId: string, params?: { event_type?: string; limit?: number }) {
  return useQuery({
    queryKey: ['events', deviceId, params],
    queryFn: () => eventsApi.listByDevice(deviceId, params),
    enabled: !!deviceId,
  })
}

export function useDeviceEventStats(deviceId: string, days?: number) {
  return useQuery({
    queryKey: ['eventStats', deviceId, days],
    queryFn: () => eventsApi.getStats(deviceId, days),
    enabled: !!deviceId,
  })
}

// Config
export function useDeviceConfig(deviceId: string) {
  return useQuery({
    queryKey: ['config', deviceId],
    queryFn: () => configApi.getDeviceConfig(deviceId),
    enabled: !!deviceId,
  })
}
